/**
 * 灰度演示入口的可见性，以及演示站识别身份用的手机号。
 *
 * 两者都取自后端接口 /user/menu-display-config/check，返回 { show, phone }：
 * - show：策略规划智能体、CMO 旗舰指挥室这类 demoOnly 入口是否对当前用户可见；
 * - phone：拼在演示站外链 phone 查询参数上的手机号。
 *
 * 取代原来的做法：名单以手机号 SHA-256 摘要配在 .env.* 的 VUE_APP_DEMO_MENU_MOBILES，
 * 前端读 localStorage 里的 USER.mobile 算摘要去比对。那套有两个毛病 ——
 * 判据在客户端，用户改一下 localStorage 就能把自己放进名单；名单要调整就得改配置重新发版。
 * 现在名单归属和手机号都由后端说了算，前端只负责问一次、缓存、按结果渲染。
 *
 * 边界：这仍然只解决「界面上要不要显示」。菜单过滤（service/api/app.js）与路由守卫
 * （router/index.js）共用本模块，是为了让「菜单里看不到」与「输 URL 进不去」口径一致；
 * 真正的授权判定必须由服务端在各业务接口上完成，不能因为这里拦住了就认为后面是安全的。
 *
 * @date 2026-09-09
 * @ai-generated true
 * @ai-tool Claude Code
 */
import { checkMenuDisplayConfig } from '@/service/api/user'
import { isOk } from './apiResult'
import { local } from './storage'

/** 取不到配置时的兜底：入口一律隐藏（失败关闭），不会误放行 */
const HIDDEN = Object.freeze({ show: false, phone: '' })

/* 路由守卫每导航到一个 demoOnly 路由就要问一次，菜单加载也要问一次，
   不缓存就是每跳一次页面发一个请求。缓存按 TOKEN 归属：换账号后 TOKEN 变了，
   下一次调用自然重新拉，不必在登出流程里额外挂清理逻辑。
   缓存的是 Promise 而不是结果，并发调用（菜单 + 守卫同时问）只会打出去一个请求。 */
let cachedToken = null
let cachedConfig = null

/**
 * 真正发一次请求并归一化返回值。
 *
 * 失败不写缓存 —— 一次网络抖动不该让灰度入口整场会话都消失，下次调用会重试。
 *
 * @param {string} token 本次请求归属的 TOKEN，用于失败时判断缓存是否还是自己的
 * @returns {Promise<{show:boolean, phone:string}>}
 */
const requestConfig = async (token) => {
  const res = await checkMenuDisplayConfig()
  if (!isOk(res)) {
    if (cachedToken === token) {
      cachedToken = null
      cachedConfig = null
    }
    return HIDDEN
  }
  /* 后端统一用 Result 包装，show/phone 在 data 里；但联调期见过直接回裸对象的情况，
     两种都认一下，免得因为包装层不一致整块功能不可用 */
  const payload = (res && 'show' in res ? res : res.data) || {}
  return {
    show: Boolean(payload.show),
    // 后端无手机号时给空串而不是 undefined，调用方直接判真假即可
    phone: payload.phone ? String(payload.phone) : ''
  }
}

/**
 * 取当前用户的菜单显示配置，同一 TOKEN 下只请求一次。
 * @returns {Promise<{show:boolean, phone:string}>} 未登录或请求失败时返回 { show:false, phone:'' }
 */
export const getMenuDisplayConfig = () => {
  const token = local.get('TOKEN')
  // 未登录不发请求：接口必然回未登录，白白触发一次拦截器的跳转
  if (!token) return Promise.resolve(HIDDEN)

  if (cachedToken !== token || !cachedConfig) {
    cachedToken = token
    cachedConfig = requestConfig(token).catch(() => {
      if (cachedToken === token) {
        cachedToken = null
        cachedConfig = null
      }
      return HIDDEN
    })
  }
  return cachedConfig
}

/**
 * 当前用户是否可见灰度演示入口。
 * @returns {Promise<boolean>}
 */
export const isDemoUser = async () => (await getMenuDisplayConfig()).show

/**
 * 演示站识别身份用的手机号。
 * @returns {Promise<string>} 后端未下发时为空串
 */
export const getDemoPhone = async () => (await getMenuDisplayConfig()).phone

/**
 * 把手机号作为 phone 查询参数拼到演示站地址后面，如
 * https://campaigndemo.solosmart.cn/manager_dashboard/index.html?phone=13900000003。
 * 策略规划智能体（VUE_APP_STRATEGY_PLANNING_URL）与营销管理指挥室（VUE_APP_CMO_DASHBOARD_URL）
 * 各自配一个完整地址，拼接共用本方法。
 *
 * 拿不到手机号时返回不带参数的原地址，而不是拼出 ?phone=undefined ——
 * 后端可能没给该用户配手机号，演示站按匿名处理即可。
 * 用 URL 拼接而不是手工判断 ? 与 &：地址将来可能自带查询串，searchParams 会自动处理分隔符与转义。
 *
 * @param {string} base 演示站地址，取自环境变量
 * @param {string} phone 手机号，空串表示没有
 * @returns {string} 拼好的地址；base 为空时为空串，地址非法时原样返回（交给调用方的校验去拒绝）
 */
export const withDemoPhone = (base, phone) => {
  if (!base || !phone) return base || ''
  try {
    const url = new URL(base)
    url.searchParams.set('phone', phone)
    return url.toString()
  } catch (error) {
    return base
  }
}
